import { Box, Container, Grid } from "@mui/material"

import { makeStyles } from "makeStyles"

import SEO from "@secureo/common/components/SEO"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"

import { helpCenterAncestorBreadCrumbs } from "./index"

import { CustomAppProps } from "pages/_app"

import Klarna from "svg/paymentMethods/klarna.svg"
import Mastercard from "svg/paymentMethods/mastercard.svg"
import Paypal from "svg/paymentMethods/paypal.svg"
import Vorkasse from "svg/paymentMethods/vorkasse.svg"
import { flatFeesByCurrencyCode } from "@secureo/common/config/shippingZonesAndCosts"

import CountryFlag from "components/i18n/CountryFlag"
import Europe from "@secureo/common/svg/flags/Europe.svg"

import Phone from "svg/contentPages/iphone11-digishop-checkout.svg"
import PaymentCircle from "svg/contentPages/payment-circle-element.svg"

const useStyles = makeStyles()({
	greyCircle: {
		position: "absolute",
		right: 0,
		top: 0,
		height: 1150,
		width: 1150,
		objectFit: "cover",
		opacity: 0.4,
	},
	blueCircle: {
		position: "absolute",
		left: 0,
		top: 550,
		height: 1300,
		objectFit: "cover",
		opacity: 0.4,
	},
	paper: {
		background: "white",
		padding: 15,
		borderRadius: 5,
		boxShadow: "3px 5px 10px 0px rgba(0,0,0,0.15)",
		border: "1px solid whitesmoke",
		minHeight: 150,
	},
	payment: {
		maxWidth: 270,
		display: "flex",
	},
	paymentRightAlign: {
		marginLeft: 100,
		marginBottom: 50,
	},
	paymentRightestAlign: {
		marginLeft: 150,
		marginBottom: 50,
	},
	phoneWrapper: {
		textAlign: "right",
		position: "relative",
		height: 600,
	},
	phone: {
		position: "absolute",
		top: -70,
		right: -35,
		width: "100%",
		height: 720,
		marginTop: 30,
		objectFit: "contain",
	},
	circle: {
		marginTop: -20,
	},
	"@media only screen and (min-width: 960px) and (max-width: 1450px)": {
		greyCircle: {
			display: "none",
		},
		blueCircle: {
			display: "none",
		},
	},
	"@media only screen and (max-width: 960px)": {
		greyCircle: {
			position: "absolute",
			right: -100,
			top: 0,
			height: 600,
			width: 600,
			objectFit: "cover",
		},
		blueCircle: {
			position: "absolute",
			left: -100,
			top: 1730,
			height: 500,
			objectFit: "cover",
		},
		phoneWrapper: {
			textAlign: "right",
			position: "relative",
			height: 450,
		},
		phone: {
			position: "absolute",
			top: -10,
			right: 0,
			width: "100%",
			height: 450,
			marginTop: "3vh",
			objectFit: "contain",
		},
		circle: {
			display: "none",
		},
		paymentRightAlign: {
			marginLeft: 0,
			marginBottom: 15,
		},
		paymentRightestAlign: {
			marginLeft: 0,
			marginBottom: 15,
		},
	},
})

const shippingPrices = [
	{
		label: "DE / AT / CH",
		icon: (
			<div style={{ display: "flex" }}>
				<Box
					style={{
						width: "20px",
						height: "100%",
						paddingTop: 4,
						marginRight: 5,
					}}
				>
					<CountryFlag countryCode="DE" />
				</Box>
				<Box
					style={{
						width: "20px",
						height: "100%",
						paddingTop: 4,
						marginRight: 5,
					}}
				>
					<CountryFlag countryCode="AT" />
				</Box>
				<Box
					style={{
						width: "20px",
						height: "100%",
						paddingTop: 4,
						marginRight: 5,
					}}
				>
					<CountryFlag countryCode="CH" />
				</Box>
			</div>
		),
		shipping: `${(flatFeesByCurrencyCode.EUR.flatFeeForSmallAmountCents / 100).toLocaleString(
			"de-De",
		)}0 €`,
		shippingFree: `${(
			flatFeesByCurrencyCode.EUR.flatFeeForSmallAmountThresholdGrossCents / 100
		).toLocaleString("de-De")} €`,
	},
	{
		label: "EU & EFTA",
		icon: (
			<Box
				style={{
					width: "25px",
					height: "100%",
					marginRight: 5,
				}}
			>
				<Europe />
			</Box>
		),
		shipping: `${(
			flatFeesByCurrencyCode.EUR.flatFeeForNonHaulageDeliveryCents / 100
		).toLocaleString("de-De")}0 € *`,
		shippingFree: null,
	},
]

const ShippingAndPaymentPage = ({ asPath, locale }: CustomAppProps) => {
	const { classes } = useStyles()

	const currentPath: BreadCrumb = {
		href: "/help/shipping-and-payment",
		label: "Versand & Zahlung",
	}

	return (
		<>
			<SEO asPath={asPath} appendLocalizedShopNameWithPipe title="Versand &amp; Zahlung" />
			<div style={{ position: "relative", overflow: "hidden" }}>
				<img
					alt="grey-circle"
					src="https://res.cloudinary.com/digitalzylinder-shop/image/upload/v1590566180/DigiShop2/Content/Bg-Grey-Circle.png"
					className={classes.greyCircle}
				/>
				<img
					alt="blue-circle"
					src="https://res.cloudinary.com/digitalzylinder-shop/image/upload/v1590566180/DigiShop2/Content/Bg-Blue-Circle.png"
					className={classes.blueCircle}
				/>
				<Container>
					<BreadCrumbs
						locale={locale}
						currentPath={currentPath}
						ancestors={helpCenterAncestorBreadCrumbs}
					/>
					<Box pt={5} mb={5} style={{ position: "relative" }}>
						<Grid container spacing={3}>
							<Grid item xs={12} sm={7}>
								<img
									alt="warum-mit-uns"
									src="https://res.cloudinary.com/digitalzylinder-shop/image/upload/v1590567707/DigiShop2/Content/Versand-und-Zahlung.png"
									style={{
										width: "100%",
										maxWidth: 650,
										height: 300,
										objectFit: "cover",
										borderRadius: 5,
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={5}>
								<div style={{ maxWidth: 450 }}>
									<div
										style={{
											color: "#4691CE",
											textTransform: "uppercase",
											fontSize: 13,
										}}
									>
										<strong>Sorgenfrei Einkaufen</strong>
									</div>
									<Box mb={2}>
										<h1 style={{ margin: 0 }}>Versand und Zahlung</h1>
									</Box>
									<p style={{ color: "#505e5f" }}>
										Hier finden Sie unsere Versand -und Zahlungsinformationen in
										der Übersicht. Außerdem steht Ihnen eine
										Versandkostenermittlung direkt im Warenkorb zur Verfügung.
									</p>
									<Box mt={2}>
										<p style={{ color: "#505e5f" }}>
											Die Lieferzeiten finden Sie direkt bei jedem Produkt.
										</p>
									</Box>
								</div>
							</Grid>
							{/* Liefergebiet Info start */}
							<Grid item xs={12}>
								<Box my={5}>
									<Grid container spacing={3}>
										<Grid item xs={12} md={3}>
											<Box mt={5} style={{ maxWidth: 350 }}>
												<h2>Liefergebiet</h2>
												<Box mt={1}>
													<p style={{ color: "#505e5f" }}>
														Wir liefern in alle Mitgliedstaaten der
														Europäischen Union (EU)*, sowie in die
														Schweiz, nach Liechtenstein und nach
														Norwegen
														<br />
														*Ausgeschlossen hiervon sind Malta, Zypern
														und Island.
													</p>
												</Box>
											</Box>
										</Grid>

										{shippingPrices.map((shippingPrice) => {
											const { label, shipping, icon, shippingFree } =
												shippingPrice
											return (
												<Grid item xs={12} md={3} key={label}>
													<Box mt={2} className={classes.paper}>
														<div
															style={{
																display: "flex",
																alignItems: "center",
																justifyContent: "space-between",
															}}
														>
															<div>
																<Box mr={1}>{icon}</Box>
															</div>
															<Box
																style={{
																	background: "#E3E5E6",
																	color: "#505e5f",
																	padding: 3,
																	paddingLeft: 10,
																	paddingRight: 10,
																	borderRadius: 5,
																	fontSize: 12,
																}}
															>
																{label}
															</Box>
														</div>
														<Box mt={2} mb={1}>
															<span style={{ color: "#505e5f" }}>
																Versandkosten
															</span>{" "}
															<br /> <strong>{shipping}</strong>
														</Box>
														{shippingFree && (
															<div>
																<span style={{ color: "#505e5f" }}>
																	Versandkostenfrei ab
																</span>{" "}
																<br />{" "}
																<strong>{shippingFree}</strong>
															</div>
														)}
													</Box>
												</Grid>
											)
										})}
										<Grid item xs={12} md={3}>
											<p>
												* Falls Sie ein Produkt mit Speditionslieferung
												bestellen, berechnen sich die Kosten abhängig von
												Gewicht &amp; Lieferland. Genauere Informationen
												können Sie auf der Produktseite einsehen.
											</p>
										</Grid>
									</Grid>
								</Box>
							</Grid>
							{/* Liefergebiet Info end */}
						</Grid>
						<Grid container spacing={3}>
							<Grid item xs={12} md={6} />
							<Grid item xs={12} md={6} style={{ maxWidth: 400 }}>
								<div
									style={{
										color: "#4691CE",
										textTransform: "uppercase",
										fontSize: 13,
									}}
								>
									<strong>Privat und Firmenkunden</strong>
								</div>
								<Box mb={2}>
									<h2 style={{ margin: 0 }}>Sichere Onlinezahlung</h2>
								</Box>
								<p style={{ color: "#505e5f" }}>
									Sie haben die Möglichkeit aus diversen, sicheren Zahlungsarten
									zu wählen.
								</p>
							</Grid>
							<Grid item xs={12} md={6}>
								<div className={classes.phoneWrapper}>
									{/* Phone */}
									<Phone className={classes.phone} style={{ zIndex: 2 }} />
									{/* Background Circle */}
									<PaymentCircle className={classes.circle} />
								</div>
							</Grid>
							<Grid item xs={12} md={6} style={{ maxWidth: 450 }}>
								<Box mt={5}>
									<div
										className={`${classes.payment} ${classes.paymentRightAlign}`}
									>
										<Box pt={3}>&#x27F6;</Box>
										<Box ml={1}>
											<div>
												<Vorkasse />
											</div>
											<strong>Vorauskasse</strong> - 2% Rabatt auf die gesamte
											Bestellung
										</Box>
									</div>
									<Box
										className={`${classes.payment} ${classes.paymentRightestAlign}`}
									>
										<Box pt={3}>&#x27F6;</Box>
										<Box ml={1}>
											<div>
												<Paypal />
											</div>
											<strong>Paypal</strong> - Über Ihr Paypal Konto einfach
											und schnell bezahlen
										</Box>
									</Box>
									<Box
										className={`${classes.payment} ${classes.paymentRightestAlign}`}
									>
										<Box pt={3}>&#x27F6;</Box>
										<Box ml={1}>
											<div>
												<Mastercard />
											</div>
											<strong>Visa &amp; Mastercard</strong> - (Die Belastung
											Ihrer Keditkarte erfolgt mit Abschluss der Bestellung)
										</Box>
									</Box>
									<div
										className={`${classes.payment} ${classes.paymentRightAlign}`}
									>
										<Box pt={3}>&#x27F6;</Box>
										<Box ml={1}>
											<div>
												<Klarna />
											</div>
											<strong>Klarna</strong> - Sofortüberweisung,
											Rechnungskauf &amp; Ratenkauf *
										</Box>
									</div>
								</Box>
								<p style={{ color: "#505e5f" }}>
									* Nur in Österreich und Deutschland verfügbar.
								</p>
							</Grid>
						</Grid>
					</Box>
				</Container>
			</div>
		</>
	)
}

export default ShippingAndPaymentPage
